import type { ReactNode } from "react";
import classNames from "classnames";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import { useLingeringValue } from "./useLingeringValue";
import { WorkDetailPane } from "./WorkDetailPane";
import styles from "./WorkDetailLayout.module.scss";

interface WorkDetailLayoutProps {
    /** The list or board. */
    children: ReactNode;
    activeRow: ClientsWorkOverviewWorkRow | null;
    /** Opened from the keyboard: the pane shows without its entrance. */
    isOpenedByKeyboard: boolean;
    onClose: () => void;
}

/**
 * The Work page's list or board with the active work item's detail pane
 * beside it. The pane's slot grows and shrinks with its slide, so the list or
 * board narrows and widens in step; the pane slides out before it unmounts.
 */
export const WorkDetailLayout = ({
    children,
    activeRow,
    isOpenedByKeyboard,
    onClose,
}: Readonly<WorkDetailLayoutProps>) => {
    const {
        value: shownRow,
        isLeaving,
        ref,
    } = useLingeringValue<ClientsWorkOverviewWorkRow, HTMLDivElement>(
        activeRow,
    );

    return (
        <div className={styles.layout}>
            <div className={styles.main}>{children}</div>
            {shownRow && (
                <div
                    ref={ref}
                    className={classNames(styles.paneSlot, {
                        [styles.leaving]: isLeaving,
                        [styles.openedByKeyboard]: isOpenedByKeyboard,
                    })}
                >
                    <WorkDetailPane row={shownRow} onClose={onClose} />
                </div>
            )}
        </div>
    );
};
